import { useState, useEffect } from 'react';
import { 
  Ticket, ArrowLeft, Plus, Trash2, Edit, AlertCircle, LayoutTemplate, Layers, 
  X, Save, Loader2, Hash, RotateCcw, AlertTriangle, Sparkles, Info 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSettings, DbTicketCategory } from '@/lib/useSettings';
import { useRole } from '@/lib/role';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { configurationsApi } from '@/api/configurationsApi';

export function TicketConfigurationPage({ 
  onBack, 
  initialTab = 'categories',
}: { 
  onBack: () => void, 
  initialTab?: string,
}) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const { role } = useRole();
  const { user } = useAuth();

  const { categories, saveCategory, deleteCategory, loading } = useSettings();

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState<Partial<DbTicketCategory>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Ticket Numbering Configuration State
  const [prefix, setPrefix] = useState('INC');
  const [startNumber, setStartNumber] = useState('1001');
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [numberLength, setNumberLength] = useState<number>(4);
  const [separator, setSeparator] = useState('-');
  const [includeYear, setIncludeYear] = useState(false);
  const [yearFormat, setYearFormat] = useState<'YYYY' | 'YY'>('YYYY');
  const [numberFormat, setNumberFormat] = useState('{PREFIX}-{NUMBER}');
  const [isActive, setIsActive] = useState(true);

  // Reset Sequence Modal State
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetTargetNumber, setResetTargetNumber] = useState('1001');
  const [isResetting, setIsResetting] = useState(false);

  // Toast notifications
  const { push: toast } = useToast();

  useEffect(() => {
    const loadNumbering = async () => {
      try {
        const res = await configurationsApi.getTicketNumberConfig(user?.organizationId || undefined);
        const data = res?.data;
        if (data) {
          setPrefix(data.prefix || 'INC');
          setStartNumber(data.startNumber ? data.startNumber.toString() : '1001');
          setCurrentNumber(data.currentNumber ?? null);
          setNumberLength(data.numberLength || 4);
          setSeparator(data.separator ?? '-');
          setIncludeYear(Boolean(data.includeYear));
          setYearFormat(data.yearFormat || 'YYYY');
          setNumberFormat(data.numberFormat || '{PREFIX}-{NUMBER}');
          setIsActive(data.isActive !== undefined ? Boolean(data.isActive) : true);
          return;
        }
      } catch (e) {
        // Fallback to legacy configurations API
        try {
          const leg = await configurationsApi.getConfiguration('ticket_numbering', user?.organizationId || undefined);
          if (leg?.value) {
            setPrefix(leg.value.prefix || 'INC');
            setStartNumber(leg.value.startNumber ? leg.value.startNumber.toString() : '1001');
            setCurrentNumber(leg.value.currentNumber ?? null);
            setNumberLength(leg.value.numberLength || 4);
            setSeparator(leg.value.separator ?? '-');
            setIncludeYear(Boolean(leg.value.includeYear));
            setYearFormat(leg.value.yearFormat || 'YYYY');
            setNumberFormat(leg.value.numberFormat || '{PREFIX}-{NUMBER}');
            setIsActive(leg.value.isActive !== undefined ? Boolean(leg.value.isActive) : true);
          }
        } catch (err) {}
      }
    };
    loadNumbering();
  }, [user?.organizationId]);

  const handleEditCategory = (cat?: DbTicketCategory) => {
    if (cat) setCategoryForm({ ...cat });
    else setCategoryForm({ name: '', description: '', is_active: true });
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) return;
    try {
      setIsSaving(true);
      await saveCategory(categoryForm);
      setIsCategoryModalOpen(false);
      toast('Category saved successfully');
    } catch (e: any) {
      console.error(e);
      toast('Failed to save category: ' + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNumbering = async () => {
    setIsSaving(true);
    try {
      const orgId = user?.organizationId || undefined;
      const numVal = parseInt(startNumber, 10) || 1001;
      const payload = {
        prefix: prefix.trim().toUpperCase() || 'INC',
        separator,
        includeYear,
        yearFormat,
        numberLength: Math.max(1, Math.min(10, numberLength || 4)),
        startNumber: numVal,
        numberFormat: numberFormat.trim() || '{PREFIX}-{NUMBER}',
        isActive
      };

      await configurationsApi.saveTicketNumberConfig(payload, orgId);
      toast('Ticket Number Configuration saved successfully!');
    } catch (e: any) {
      console.error(e);
      toast('Failed to save configuration: ' + (e.message || 'Server error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSequence = async () => {
    const target = parseInt(resetTargetNumber, 10);
    if (isNaN(target) || target < 1) {
      toast('Please enter a valid positive number for the sequence reset.');
      return;
    }

    setIsResetting(true);
    try {
      const orgId = user?.organizationId || undefined;
      const res = await configurationsApi.resetTicketNumberConfig(target, orgId);
      if (res?.success) {
        toast(res.message || 'Sequence reset successfully!');
        setStartNumber(target.toString());
        setCurrentNumber(target - 1);
        setIsResetModalOpen(false);
      } else {
        toast(res?.error || 'Failed to reset sequence.');
      }
    } catch (e: any) {
      console.error(e);
      const msg = e?.response?.data?.error || e?.message || 'Collision detected';
      toast('Reset failed: ' + msg);
    } finally {
      setIsResetting(false);
    }
  };

  const previewTicketNumber = () => {
    const currentYear = new Date().getFullYear();
    const yearStr = yearFormat === 'YY' ? String(currentYear).slice(-2) : String(currentYear);
    const length = Math.max(1, Math.min(10, numberLength || 4));
    const nextSeq = currentNumber ? currentNumber + 1 : parseInt(startNumber, 10) || 1001;
    const padded = String(nextSeq).padStart(length, '0');
    let tmpl = (numberFormat || (includeYear ? '{PREFIX}{SEP}{YEAR}{SEP}{NUMBER}' : '{PREFIX}{SEP}{NUMBER}')).trim();
    return tmpl
      .replace(/\{PREFIX\}/gi, (prefix || 'INC').trim().toUpperCase())
      .replace(/\{SEP\}/gi, separator || '-')
      .replace(/\{YEAR\}/gi, yearStr)
      .replace(/\{NUMBER\}/gi, padded)
      .replace(/\{YYYY\}/gi, String(currentYear))
      .replace(/\{YY\}/gi, String(currentYear).slice(-2));
  };

  // Mock Priorities (Read Only)
  const priorities = [
    { id: '1', name: 'Low', color: 'slate', sla: '48h', description: 'General questions, no work stoppage' },
    { id: '2', name: 'Medium', color: 'blue', sla: '24h', description: 'Partial work stoppage, workaround exists' },
    { id: '3', name: 'High', color: 'orange', sla: '4h', description: 'Severe work stoppage, affecting multiple users' },
    { id: '4', name: 'Urgent', color: 'red', sla: '1h', description: 'Total system outage or security breach' },
  ];

  // Mock Statuses (Read Only)
  const statuses = [
    { id: '1', name: 'Open', category: 'To Do' },
    { id: '2', name: 'In Progress', category: 'In Progress' },
    { id: '3', name: 'Pending Customer', category: 'Waiting' },
    { id: '4', name: 'Pending Vendor', category: 'Waiting' },
    { id: '5', name: 'Resolved', category: 'Done' },
    { id: '6', name: 'Closed', category: 'Done' },
  ];

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const tabs = [
    { id: 'categories', label: 'Categories', icon: Layers },
    { id: 'priorities', label: 'Priorities', icon: AlertCircle },
    { id: 'statuses', label: 'Statuses', icon: LayoutTemplate },
  ];

  const isAdmin = role === 'Super Admin' || role === 'Admin' || (role && role.toLowerCase().includes('admin'));
  if (isAdmin) {
    tabs.push({ id: 'numbering', label: 'Ticket Numbering', icon: Hash });
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-4">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500">
          <Ticket className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight">Ticket Configuration</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Manage ticket categories, priorities, statuses, and custom fields.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-muted/10 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto w-full max-w-7xl space-y-6">
          
          <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                  activeTab === tab.id ? "bg-card shadow-sm border border-border text-foreground" : "text-muted-foreground hover:bg-muted"
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'categories' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg font-medium">Ticket Categories</h3>
                  <p className="text-sm text-muted-foreground mt-1">Classify incoming requests to route them effectively.</p>
                </div>
                <button 
                  onClick={() => handleEditCategory()}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 shadow-sm"
                >
                  <Plus className="h-4 w-4" /> Add Category
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {categories.map(cat => (
                  <div key={cat.id} className="relative rounded-xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-foreground">{cat.name}</div>
                        {!cat.is_active && (
                          <span className="inline-flex items-center rounded-full bg-slate-500/10 px-2 py-0.5 text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEditCategory(cat)} className="p-1.5 text-muted-foreground hover:text-primary transition rounded hover:bg-primary/10"><Edit className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteCategory(cat.id)} className="p-1.5 text-muted-foreground hover:text-rose-500 transition rounded hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{cat.description || 'No description provided.'}</p>
                  </div>
                ))}
              </div>
              {categories.length === 0 && (
                <div className="py-12 text-center border border-dashed border-border rounded-xl bg-card">
                  <Layers className="h-8 w-8 mx-auto text-muted-foreground mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">No categories defined. Click Add Category to get started.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'priorities' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg font-medium">Priority Levels</h3>
                  <p className="text-sm text-muted-foreground mt-1">System-locked priority levels used for SLA calculation.</p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">Level</th>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">Target SLA</th>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {priorities.map((p) => (
                      <tr key={p.id}>
                        <td className="px-6 py-4 font-medium">
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
                            p.color === 'slate' ? 'bg-slate-500/10 text-slate-500 border-slate-500/20' :
                            p.color === 'blue' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' :
                            p.color === 'orange' ? 'bg-orange-500/10 text-orange-500 border-orange-500/20' :
                            'bg-red-500/10 text-red-500 border-red-500/20'
                          )}>{p.name}</span>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs">{p.sla}</td>
                        <td className="px-6 py-4 text-muted-foreground">{p.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'statuses' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg font-medium">Ticket Statuses</h3>
                  <p className="text-sm text-muted-foreground mt-1">System-locked statuses mapped to standard workflows.</p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">Status Name</th>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">Category</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {statuses.map((s) => (
                      <tr key={s.id}>
                        <td className="px-6 py-4 font-medium">{s.name}</td>
                        <td className="px-6 py-4 text-muted-foreground">
                          <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium">
                            {s.category}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'numbering' && isAdmin && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
                    <Hash className="h-5 w-5 text-primary" />
                    Ticket Number Configuration
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure the centralized ticket prefix, number format, sequence length, and starting counter stored in Supabase.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => {
                      setResetTargetNumber(String((currentNumber || 1000) + 1));
                      setIsResetModalOpen(true);
                    }}
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-medium text-foreground hover:bg-muted transition shadow-sm"
                  >
                    <RotateCcw className="h-4 w-4 text-muted-foreground" />
                    Reset Sequence
                  </button>
                  <button 
                    onClick={handleSaveNumbering}
                    disabled={isSaving}
                    type="button"
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 shadow-sm disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save Configuration
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Column: Configuration Settings */}
                <div className="lg:col-span-7 space-y-6">
                  <div className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-5">
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-3 flex items-center justify-between">
                      <span>Format Settings</span>
                      <span className="text-xs font-normal capitalize px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        Single Source of Truth
                      </span>
                    </h4>

                    {/* Ticket Prefix */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Ticket Prefix *
                      </label>
                      <input 
                        value={prefix}
                        onChange={e => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                        className="w-full sm:w-64 rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono font-medium outline-none transition focus:border-primary" 
                        placeholder="e.g. INC, REQ, TKT, SUP"
                        maxLength={10}
                      />
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        Alphanumeric identifier prepended to tickets (e.g. <span className="font-mono text-foreground font-medium">INC</span>, <span className="font-mono text-foreground font-medium">REQ</span>).
                      </p>
                    </div>

                    {/* Number Format Template */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Number Format Template
                      </label>
                      <input 
                        value={numberFormat}
                        onChange={e => setNumberFormat(e.target.value)}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono outline-none transition focus:border-primary" 
                        placeholder="e.g. {PREFIX}-{NUMBER} or {PREFIX}-{YEAR}-{NUMBER}"
                      />
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="text-muted-foreground py-0.5">Quick presets:</span>
                        <button
                          type="button"
                          onClick={() => { setNumberFormat('{PREFIX}-{NUMBER}'); setIncludeYear(false); }}
                          className="px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-foreground font-mono transition"
                        >
                          {'{PREFIX}-{NUMBER}'} (Recommended)
                        </button>
                        <button
                          type="button"
                          onClick={() => { setNumberFormat('{PREFIX}-{YEAR}-{NUMBER}'); setIncludeYear(true); }}
                          className="px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-foreground font-mono transition"
                        >
                          {'{PREFIX}-{YEAR}-{NUMBER}'}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Starting Number */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Starting Number *
                        </label>
                        <input 
                          type="number"
                          min="1"
                          value={startNumber}
                          onChange={e => setStartNumber(e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono outline-none transition focus:border-primary" 
                          placeholder="e.g. 1001"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">Base sequence starting value.</p>
                      </div>

                      {/* Number Length / Padding */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Number Length (Digits)
                        </label>
                        <input 
                          type="number"
                          min="1"
                          max="10"
                          value={numberLength}
                          onChange={e => setNumberLength(parseInt(e.target.value, 10) || 4)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono outline-none transition focus:border-primary" 
                          placeholder="e.g. 4 for 1001, 6 for 000001"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">Minimum number of digits with zero padding.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Separator */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Separator
                        </label>
                        <input 
                          value={separator}
                          onChange={e => setSeparator(e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono outline-none transition focus:border-primary" 
                          placeholder="-"
                          maxLength={3}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">Character separating tokens (e.g. -, /, .).</p>
                      </div>

                      {/* Year Format */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Year Format
                        </label>
                        <select 
                          value={yearFormat}
                          onChange={e => setYearFormat(e.target.value as 'YYYY' | 'YY')}
                          disabled={!includeYear}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary disabled:opacity-50"
                        >
                          <option value="YYYY">YYYY (e.g. {new Date().getFullYear()})</option>
                          <option value="YY">YY (e.g. {String(new Date().getFullYear()).slice(-2)})</option>
                        </select>
                        <p className="mt-1 text-xs text-muted-foreground">Applies when Year token is included.</p>
                      </div>
                    </div>

                    {/* Toggle Options */}
                    <div className="pt-2 border-t border-border space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20">
                        <div>
                          <div className="text-sm font-medium text-foreground">Include Year in Ticket Number</div>
                          <div className="text-xs text-muted-foreground">Embed the current creation year into the formatted ID</div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => setIncludeYear(!includeYear)}
                          className={cn(
                            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                            includeYear ? "bg-primary" : "bg-muted"
                          )}
                        >
                          <span className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            includeYear ? "translate-x-4" : "translate-x-1"
                          )} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20">
                        <div>
                          <div className="text-sm font-medium text-foreground">Active Configuration</div>
                          <div className="text-xs text-muted-foreground">Enable this numbering configuration for new tickets</div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => setIsActive(!isActive)}
                          className={cn(
                            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                            isActive ? "bg-primary" : "bg-muted"
                          )}
                        >
                          <span className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            isActive ? "translate-x-4" : "translate-x-1"
                          )} />
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Right Column: Live Preview & Sequence Status */}
                <div className="lg:col-span-5 space-y-6">
                  {/* Live Preview Box */}
                  <div className="rounded-xl border border-primary/30 bg-card p-6 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 transform translate-x-4 -translate-y-4 w-24 h-24 bg-primary/10 rounded-full blur-xl pointer-events-none"></div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">Live Generated Preview</span>
                    </div>

                    <div className="rounded-lg bg-primary/5 border border-primary/20 p-5 text-center my-3">
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Next Generated Ticket</div>
                      <div className="text-3xl font-mono font-bold text-primary tracking-tight">
                        {previewTicketNumber()}
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground text-center">
                      This is the exact ticket identifier that Normal Users, Technicians, Emails, and Notifications will receive.
                    </p>

                    {/* Next 3 Sequence Preview */}
                    <div className="mt-5 pt-4 border-t border-border">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
                        Upcoming Sequence Samples
                      </div>
                      <div className="space-y-1.5 font-mono text-xs">
                        {[0, 1, 2].map((offset) => {
                          const base = currentNumber ? currentNumber + 1 : parseInt(startNumber, 10) || 1001;
                          const seq = base + offset;
                          const length = Math.max(1, Math.min(10, numberLength || 4));
                          const padded = String(seq).padStart(length, '0');
                          const currentYear = new Date().getFullYear();
                          const yearStr = yearFormat === 'YY' ? String(currentYear).slice(-2) : String(currentYear);
                          let tmpl = (numberFormat || (includeYear ? '{PREFIX}{SEP}{YEAR}{SEP}{NUMBER}' : '{PREFIX}{SEP}{NUMBER}')).trim();
                          const sampleNum = tmpl
                            .replace(/\{PREFIX\}/gi, (prefix || 'INC').trim().toUpperCase())
                            .replace(/\{SEP\}/gi, separator || '-')
                            .replace(/\{YEAR\}/gi, yearStr)
                            .replace(/\{NUMBER\}/gi, padded)
                            .replace(/\{YYYY\}/gi, String(currentYear))
                            .replace(/\{YY\}/gi, String(currentYear).slice(-2));

                          return (
                            <div key={offset} className="flex items-center justify-between px-3 py-1.5 rounded bg-muted/40 text-foreground">
                              <span className="text-muted-foreground font-sans">Ticket #{offset + 1}</span>
                              <span className="font-semibold text-primary">{sampleNum}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Current DB State */}
                    <div className="mt-5 pt-4 border-t border-border space-y-2 text-xs">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Database Key:</span>
                        <span className="font-mono text-foreground">configurations (ticket_numbering)</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Organization:</span>
                        <span className="font-medium text-foreground">
                          {user?.organizationName || 'Pinochle / Rezilyens'}
                        </span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Current Sequence Counter:</span>
                        <span className="font-mono font-bold text-foreground">
                          {currentNumber !== null ? currentNumber : 'Unset (Uses Start Number)'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Architecture Info Callout */}
                  <div className="rounded-xl border border-border bg-muted/30 p-4 text-xs text-muted-foreground space-y-2">
                    <div className="flex items-center gap-1.5 font-semibold text-foreground">
                      <Info className="h-4 w-4 text-primary" />
                      Concurrency-Safe Atomic Counter
                    </div>
                    <p>
                      Ticket numbers are assigned exclusively by the Backend API and Cloudflare Worker using transaction-safe atomic synchronization in Supabase. The React frontend never fabricates ticket numbers independently.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Modals */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold">{categoryForm.id ? 'Edit Category' : 'New Category'}</h2>
              <button onClick={() => setIsCategoryModalOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Category Name *</label>
                <input 
                  value={categoryForm.name || ''} 
                  onChange={e => setCategoryForm({...categoryForm, name: e.target.value})}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  placeholder="e.g. Hardware Issues"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</label>
                <textarea 
                  value={categoryForm.description || ''} 
                  onChange={e => setCategoryForm({...categoryForm, description: e.target.value})}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary h-24 resize-none" 
                  placeholder="Brief description of what falls under this category..."
                />
              </div>

              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div>
                  <div className="font-medium text-sm">Active Category</div>
                  <div className="text-xs text-muted-foreground">Allow new tickets to use this category</div>
                </div>
                <button 
                  onClick={() => setCategoryForm({...categoryForm, is_active: !categoryForm.is_active})}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${categoryForm.is_active ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${categoryForm.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button 
                onClick={() => setIsCategoryModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveCategory}
                disabled={isSaving || !categoryForm.name}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Sequence Confirmation Modal */}
      {isResetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5 text-amber-600 dark:text-amber-500">
                <AlertTriangle className="h-5 w-5" />
                <h2 className="font-display text-lg font-semibold text-foreground">Reset Ticket Sequence</h2>
              </div>
              <button 
                onClick={() => setIsResetModalOpen(false)} 
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-3.5 rounded-lg border border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-200 text-xs leading-relaxed">
                <span className="font-semibold block mb-1">Warning: Sequence Reset Impact</span>
                Resetting the ticket number sequence can cause duplicate references if an existing number is reused. Continue?
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed">
                The database will verify that the sequence number is safe and does not overwrite existing tickets with the current prefix (<span className="font-mono font-medium text-foreground">{prefix}</span>).
              </p>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  New Starting / Next Sequence Number *
                </label>
                <input 
                  type="number"
                  min="1"
                  value={resetTargetNumber}
                  onChange={e => setResetTargetNumber(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono outline-none transition focus:border-primary" 
                  placeholder="e.g. 2000"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Next generated ticket will be: <span className="font-mono font-semibold text-primary">{prefix}-{resetTargetNumber}</span>
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button 
                type="button"
                onClick={() => setIsResetModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={handleResetSequence}
                disabled={isResetting || !resetTargetNumber}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-700 px-4 py-2 text-sm font-medium text-white transition disabled:opacity-50"
              >
                {isResetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Confirm Reset Sequence
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
